import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { db, auth } from '../firebaseConfig';
import {
    doc,
    getDoc,
    getDocs,
    collection,
    setDoc,
    updateDoc,
    DocumentData,
    query, where, limit,
    serverTimestamp
} from 'firebase/firestore';
import Toast from './Toast';
import ConfirmationModal from './modals/ConfirmationModalActions';

import { setPipelineRefresh } from '../redux/pipelineRefresh';
import { useAppSelector, useAppDispatch } from '../redux/store';


interface GradeData {
    completionYear: number;
    percentage: string;
    stream: string;
    syllabus: string;
}

interface HighestEducationData {
    id: string;
    name: string;
    completionYear: string;
    marksObtained: string;
}

interface AcademicData {
    tenthGrade: GradeData;
    twelfthGrade: GradeData;
    highestEducation: HighestEducationData[];
}

interface StudyPreferenceData {
    destinationId: string;
    destinationName: string;
    intendedCourseLevel: string;
    intendedAreaOfStudy: string;
    intakeMonth: string;
    intakeYear: number;
}

interface CustomClaims {
    leadUid: string;
    subAgentId: string;
    companyId: string;
    role: string;
}

interface AcademicResultsProps {
    write: boolean;
}

const AcademicResults = ({ write }: AcademicResultsProps) => {
    const { leadId: urlLeadId } = useParams();
    const [leadId, setLeadId] = useState(urlLeadId || "");
    const [leadName, setLeadName] = useState("");
    const [leadPhone, setLeadPhone] = useState("");
    const [companyId, setCompanyId] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [toastMessage, setToastMessage] = useState('');
    const [isErrorToast, setIsErrorToast] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [academicData, setAcademicData] = useState<AcademicData>({
        tenthGrade: {
            completionYear: 0,
            percentage: '',
            stream: '',
            syllabus: '',
        },
        twelfthGrade: {
            completionYear: 0,
            percentage: '',
            stream: '',
            syllabus: '',
        },
        highestEducation: [{
            id: '1',
            name: '',
            completionYear: '',
            marksObtained: '',
        }]
    });

    const [studyPreference, setStudyPreference] = useState<StudyPreferenceData>({
        destinationId: '',
        destinationName: '',
        intendedCourseLevel: '',
        intendedAreaOfStudy: '',
        intakeMonth: '',
        intakeYear: new Date().getFullYear()
    });

    // Removed dropdown state since year fields are now inputs

    const destinationRef = React.createRef<HTMLDivElement>();
    const intakeMonthRef = React.createRef<HTMLDivElement>();
    const intakeYearRef = React.createRef<HTMLDivElement>();

    const [countries, setCountries] = useState<{ id: string; name: string; }[]>([]);
    const [isDestinationDropdownOpen, setIsDestinationDropdownOpen] = useState(false);
    const [isIntakeMonthDropdownOpen, setIsIntakeMonthDropdownOpen] = useState(false);
    const [isIntakeYearDropdownOpen, setIsIntakeYearDropdownOpen] = useState(false);

    const [studentName, setStudentName] = useState("Loading...");
    const [studentId, setStudentId] = useState("");
    const [companyName, setCompanyName] = useState("");

    // Percentage validation states
    const [percentageErrors, setPercentageErrors] = useState({
        tenthGrade: false,
        twelfthGrade: false,
    });

    // Year validation states
    const [yearErrors, setYearErrors] = useState({
        tenthGrade: false,
        twelfthGrade: false,
    });

    // Highest education year validation states
    const [educationYearErrors, setEducationYearErrors] = useState<Record<string, boolean>>({});

    const pipelineRefresh = useAppSelector(state => state.pipelineRefresh.refresh);
    const dispatch = useAppDispatch();

    // modal states
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isAdditionalActions, setAdditionalActions] = useState(false);

    const currentYear = new Date().getFullYear();

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const intakeYears = Array.from(
        { length: 11 },
        (_, i) => new Date().getFullYear() + i
    );

    const fetchAcademicData = async (companyId: string) => {
        try {
            if (!companyId || !leadId) return;

            // Fetch 10th grade data
            const tenthGradeDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}/academics/10thGrade`));
            // Fetch 12th grade data
            const twelfthGradeDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}/academics/12thGrade`));
            // Fetch highest education data
            const highestEducationDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}/academics/HighestLevelOfEducation`));
            
            let highestEducationData: HighestEducationData[];
            if (highestEducationDoc.exists()) {
                const data = highestEducationDoc.data();
                // Check if data is already an array or convert single object to array
                if (Array.isArray(data.entries)) {
                    highestEducationData = data.entries;
                } else {
                    // Convert old single object format to array format
                    highestEducationData = [{
                        id: '1',
                        name: data.name || '',
                        completionYear: data.completionYear || '',
                        marksObtained: data.marksObtained || ''
                    }];
                }
            } else {
                highestEducationData = academicData.highestEducation;
            }

            setAcademicData({
                tenthGrade: tenthGradeDoc.exists() ? {
                    ...academicData.tenthGrade,
                    ...tenthGradeDoc.data() as GradeData
                } : academicData.tenthGrade,
                twelfthGrade: twelfthGradeDoc.exists() ? {
                    ...academicData.twelfthGrade,
                    ...twelfthGradeDoc.data() as GradeData
                } : academicData.twelfthGrade,
                highestEducation: highestEducationData,
            });
        } catch (error) {
            console.error("Error fetching academic data:", error);
        }
    };

    const fetchStudyPreferenceData = async (companyId: string) => {
        try {
            // Fetch countries
            const countriesSnapshot = await getDocs(collection(db, 'countries'));
            const countriesData = countriesSnapshot.docs.map((doc: DocumentData) => ({
                id: doc.id,
                name: doc.data().name
            }));
            setCountries(countriesData);

            // Fetch current lead data
            const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
            if (leadDoc.exists()) {
                const data = leadDoc.data();

                // Set destination
                if (data.destinationId) {
                    const country = countriesData.find(c => c.id === data.destinationId);
                    if (country) {
                        setStudyPreference(prev => ({
                            ...prev,
                            destinationId: data.destinationId,
                            destinationName: country.name,
                            intendedCourseLevel: data.intendedCourseLevel || '',
                            intendedAreaOfStudy: data.intendedAreaOfStudy || '',
                            intakeMonth: data.intakeMonth || '',
                            intakeYear: data.intakeYear || new Date().getFullYear()
                        }));
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching study preference data:", error);
        }
    };

    const [countrySearchTerm, setCountrySearchTerm] = useState('');
    const [selectedCountryName, setSelectedCountryName] = useState('');
    const [isLoadingCountry, setIsLoadingCountry] = useState(false);

    const handleCountrySearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        setStudyPreference(prev => ({
            ...prev,
            destinationId: '',
            destinationName: ''
        }));
        setCountrySearchTerm(e.target.value);

        if (value.length < 3) {
            setCountries([]);
            return;
        }

        setIsLoadingCountry(true);
        try {
            const countriesRef = collection(db, 'countries');
            const q = query(
                countriesRef,
                where('name', '>=', value),
                where('name', '<=', value + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const countriesData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                name: doc.data().name
            }));

            setCountries(countriesData);
        } catch (error) {
            console.error("Error searching countries:", error);
        } finally {
            setIsLoadingCountry(false);
        }
    }

    const handleGradeDataChange = (grade: '10thGrade' | '12thGrade', field: keyof GradeData, value: number | string) => {
        // Update local state only
        setAcademicData(prev => ({
            ...prev,
            [grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade']: {
                ...prev[grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade'],
                [field]: field === 'completionYear' ? Number(value) : 
                        field === 'percentage' ? `${value}%` : 
                        String(value)
            }
        }));
    };

    const handleYearChange = (grade: '10thGrade' | '12thGrade', value: string) => {
        // Only allow numeric characters and limit to 4 digits
        const numericValue = value.replace(/[^0-9]/g, '').slice(0, 4);
        
        // Update the academic data
        setAcademicData(prev => ({
            ...prev,
            [grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade']: {
                ...prev[grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade'],
                completionYear: numericValue ? Number(numericValue) : 0
            }
        }));
    };

    const validateYear = (grade: '10thGrade' | '12thGrade', value: string | number) => {
        const yearString = String(value);
        const isInvalid = yearString.length > 0 && yearString.length !== 4;
        
        setYearErrors(prev => ({
            ...prev,
            [grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade']: isInvalid
        }));
        
        return !isInvalid;
    };

    const handleEducationYearChange = (id: string, value: string) => {
        // Only allow numeric characters and limit to 4 digits
        const numericValue = value.replace(/[^0-9]/g, '').slice(0, 4);
        
        // Update the highest education data
        setAcademicData(prev => ({
            ...prev,
            highestEducation: prev.highestEducation.map(edu => 
                edu.id === id ? { ...edu, completionYear: numericValue } : edu
            )
        }));
    };

    const validateEducationYear = (id: string, value: string) => {
        const isInvalid = value.length > 0 && value.length !== 4;
        
        setEducationYearErrors(prev => ({
            ...prev,
            [id]: isInvalid
        }));
        
        return !isInvalid;
    };

    const handlePercentageChange = (grade: '10thGrade' | '12thGrade', value: string) => {
        // Remove % symbol and any non-numeric characters except decimal point
        const numericValue = value.replace(/[^0-9.]/g, '');
        
        // Update the academic data
        setAcademicData(prev => ({
            ...prev,
            [grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade']: {
                ...prev[grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade'],
                percentage: numericValue ? `${numericValue}%` : ''
            }
        }));
    };

    const validatePercentage = (grade: '10thGrade' | '12thGrade', value: string) => {
        const numericValue = parseFloat(value.replace('%', ''));
        const isInvalid = !isNaN(numericValue) && numericValue > 100;
        
        setPercentageErrors(prev => ({
            ...prev,
            [grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade']: isInvalid
        }));
        
        return !isInvalid;
    };

    const handleHighestEducationChange = (id: string, field: keyof Omit<HighestEducationData, 'id'>, value: string) => {
        setAcademicData(prev => ({
            ...prev,
            highestEducation: prev.highestEducation.map(edu => 
                edu.id === id ? { ...edu, [field]: value } : edu
            )
        }));
    };

    const addHighestEducationEntry = () => {
        const newId = Date.now().toString(); // Simple ID generation
        setAcademicData(prev => ({
            ...prev,
            highestEducation: [...prev.highestEducation, {
                id: newId,
                name: '',
                completionYear: '',
                marksObtained: ''
            }]
        }));
        
        // Clear any existing validation error for the new entry
        setEducationYearErrors(prev => ({
            ...prev,
            [newId]: false
        }));
    };

    const removeHighestEducationEntry = (id: string) => {
        setAcademicData(prev => ({
            ...prev,
            highestEducation: prev.highestEducation.filter(edu => edu.id !== id)
        }));
        
        // Remove validation error for the deleted entry
        setEducationYearErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[id];
            return newErrors;
        });
    };

    useEffect(() => {
        if (leadId) {
            const fetchLeadData = async () => {
                try {
                    const leadDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}`));
                    if (leadDoc.exists()) {
                        const data = leadDoc.data();
                        setLeadName(data.name);
                        setLeadPhone(data.phone);
                        setStudentId(leadId);
                    }
                } catch (error) {
                    console.error("Error fetching lead data:", error);
                }
            };
            fetchLeadData();
        }
    }, [leadId]);

    const confirmSave = () => {
        // Open confirmation modal
        setIsConfirmModalOpen(true);
    }


    const handleSave = async () => {
        try {
            setIsLoading(true);
            if (!companyId || !leadId) return;

            // Validate percentages and years before saving
            const tenthPercentage = academicData.tenthGrade.percentage;
            const twelfthPercentage = academicData.twelfthGrade.percentage;
            const tenthYear = academicData.tenthGrade.completionYear;
            const twelfthYear = academicData.twelfthGrade.completionYear;
            
            let hasValidationErrors = false;

            if (tenthPercentage) {
                const isValid = validatePercentage('10thGrade', tenthPercentage);
                if (!isValid) hasValidationErrors = true;
            }

            if (twelfthPercentage) {
                const isValid = validatePercentage('12thGrade', twelfthPercentage);
                if (!isValid) hasValidationErrors = true;
            }

            if (tenthYear) {
                const isValid = validateYear('10thGrade', tenthYear);
                if (!isValid) hasValidationErrors = true;
            }

            if (twelfthYear) {
                const isValid = validateYear('12thGrade', twelfthYear);
                if (!isValid) hasValidationErrors = true;
            }

            // Validate highest education completion years
            for (const education of academicData.highestEducation) {
                if (education.completionYear) {
                    const isValid = validateEducationYear(education.id, education.completionYear);
                    if (!isValid) hasValidationErrors = true;
                }
            }

            if (hasValidationErrors) {
                setToastMessage('Please correct the validation errors. Years must be 4 digits and percentages cannot exceed 100%.');
                setIsErrorToast(true);
                setShowToast(true);
                setIsLoading(false);
                setIsConfirmModalOpen(false);
                return;
            }

            // Helper function to sanitize data (convert empty strings to null)
            const sanitizeData = (data: any) => {
                const sanitized: Record<string, any> = {};
                Object.keys(data).forEach(key => {
                    sanitized[key] = (typeof data[key] === 'string' && data[key].trim() === '')
                        ? null
                        : data[key];
                });
                return sanitized;
            };

            // Save 10th grade data (with sanitization)
            await setDoc(
                doc(db, `companies/${companyId}/leads/${leadId}/academics/10thGrade`),
                sanitizeData(academicData.tenthGrade)
            );

            // Save 12th grade data (with sanitization)
            await setDoc(
                doc(db, `companies/${companyId}/leads/${leadId}/academics/12thGrade`),
                sanitizeData(academicData.twelfthGrade)
            );

            // Save highest education data (with sanitization)
            const sanitizedHighestEducation = {
                entries: academicData.highestEducation.map(edu => sanitizeData({
                    id: edu.id,
                    name: edu.name,
                    completionYear: edu.completionYear,
                    marksObtained: edu.marksObtained
                }))
            };
            
            await setDoc(
                doc(db, `companies/${companyId}/leads/${leadId}/academics/HighestLevelOfEducation`),
                sanitizedHighestEducation
            );

            // Sanitize study preference data
            const sanitizedStudyPreference = sanitizeData({
                destinationId: studyPreference.destinationId,
                intendedCourseLevel: studyPreference.intendedCourseLevel,
                intendedAreaOfStudy: studyPreference.intendedAreaOfStudy,
                intakeMonth: studyPreference.intakeMonth,
                intakeYear: studyPreference.intakeYear,
            });

            // Save study preference data
            const leadRef = doc(db, `companies/${companyId}/leads/${leadId}`);
            await updateDoc(leadRef, {
                ...sanitizedStudyPreference,
                updatedBy: auth.currentUser?.uid,
                updatedAt: serverTimestamp(),
            });

            setToastMessage('Academic details and study preferences saved successfully!');
            setIsErrorToast(false);
            setShowToast(true);
            // Refresh the pipeline view if needed
            dispatch(setPipelineRefresh(true));
            console.log('Academic details and study preferences saved successfully');
        } catch (error) {
            console.error("Error saving details:", error);
            setToastMessage('Error saving details. Please try again.');
            setIsErrorToast(true);
            setShowToast(true);
        } finally {
            setIsLoading(false);
            setIsConfirmModalOpen(false);
            setAdditionalActions(true);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                destinationRef.current &&
                !destinationRef.current.contains(event.target as Node)
            ) {
                setIsDestinationDropdownOpen(false);
            }
            if (
                intakeMonthRef.current &&
                !intakeMonthRef.current.contains(event.target as Node)
            ) {
                setIsIntakeMonthDropdownOpen(false);
            }
            if (
                intakeYearRef.current &&
                !intakeYearRef.current.contains(event.target as Node)
            ) {
                setIsIntakeYearDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const currentUser = auth.currentUser;
                if (!currentUser) return;

                // Get token claims
                const idTokenResult = await currentUser.getIdTokenResult();
                let companyId;

                // Check if we have student claims
                const claims = idTokenResult.claims as unknown as CustomClaims;
                if (claims.leadUid && claims.companyId) {
                    // For student, use claims
                    setLeadId(claims.leadUid);
                    companyId = claims.companyId;
                } else if (claims.subAgentId && claims.companyId) {
                    // For admin, use claims
                    companyId = claims.companyId;
                } else {
                    // For regular users, use URL param and user document
                    if (!urlLeadId) return; // regular users must have leadId in URL
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    companyId = userDoc.data()?.companyId;
                }

                if (!companyId || !leadId) return;
                setCompanyId(companyId);

                const academicResultsDoc = await getDoc(doc(db, `companies/${companyId}/leads/${leadId}/academic_results/results`));

                await fetchAcademicData(companyId);
                await fetchStudyPreferenceData(companyId);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [leadId]);

    const inputClass = `w-full py-3 px-4 bg-white border border-cyan-600 rounded-3xl focus:outline-none focus:ring-2 focus:ring-sky-950 ${!write ? 'cursor-not-allowed' : 'cursor-pointer'}`;
    const labelClass = "block font-montserrat font-normal text-base text-black mb-2";

    const renderPercentageInput = (
        grade: '10thGrade' | '12thGrade',
        label: string,
        placeholder: string
    ) => {
        const gradeKey = grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade';
        const errorKey = grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade';
        const currentValue = academicData[gradeKey].percentage || '';
        const hasError = percentageErrors[errorKey];

        return (
            <div>
                <label className={labelClass}>{label}</label>
                <div className="relative">
                    <input
                        type="text"
                        value={currentValue}
                        onChange={(e) => handlePercentageChange(grade, e.target.value)}
                        onBlur={(e) => validatePercentage(grade, e.target.value)}
                        placeholder={placeholder}
                        className={`${inputClass} ${hasError ? 'border-red-500 focus:ring-red-500' : ''}`}
                        readOnly={!write}
                        disabled={!write}
                    />
                    {hasError && (
                        <div className="absolute -bottom-6 left-0 text-red-500 text-sm">
                            Percentage cannot exceed 100%
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderTextInput = (
        grade: '10thGrade' | '12thGrade',
        field: 'stream' | 'syllabus',
        label: string,
        placeholder: string
    ) => {
        const gradeKey = grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade';
        const currentValue = academicData[gradeKey][field] || '';

        return (
            <div>
                <label className={labelClass}>{label}</label>
                <input
                    type="text"
                    value={currentValue}
                    onChange={(e) => handleGradeDataChange(grade, field, e.target.value)}
                    placeholder={placeholder}
                    className={inputClass}
                    readOnly={!write}
                    disabled={!write}
                />
            </div>
        );
    };

    const renderYearInput = (
        grade: '10thGrade' | '12thGrade',
        label: string,
        placeholder: string
    ) => {
        const gradeKey = grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade';
        const errorKey = grade === '10thGrade' ? 'tenthGrade' : 'twelfthGrade';
        const currentValue = academicData[gradeKey].completionYear || '';
        const hasError = yearErrors[errorKey];

        return (
            <div>
                <label className={labelClass}>{label}</label>
                <div className="relative">
                    <input
                        type="text"
                        value={currentValue === 0 ? '' : currentValue}
                        onChange={(e) => handleYearChange(grade, e.target.value)}
                        onBlur={(e) => validateYear(grade, e.target.value)}
                        placeholder={placeholder}
                        maxLength={4}
                        className={`${inputClass} ${hasError ? 'border-red-500 focus:ring-red-500' : ''}`}
                        readOnly={!write}
                        disabled={!write}
                    />
                    {hasError && (
                        <div className="absolute -bottom-6 left-0 text-red-500 text-sm">
                            Year must be exactly 4 digits
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div>
            <div>
                <h3 className="text-xl font-semibold mb-4">Past Academics</h3>

                {/* 10th Grade Section */}
                <label className={labelClass}>10th Grade - Year, Percentage, Stream & Syllabus</label>
                <div className="mt-6 grid grid-cols-4 gap-6 w-[90%] mb-8">
                    {renderYearInput(
                        '10thGrade',
                        'Year',
                        'Enter year (e.g., 2020)'
                    )}
                    {renderPercentageInput(
                        '10thGrade',
                        'Percentage',
                        'Enter percentage'
                    )}
                    {renderTextInput(
                        '10thGrade',
                        'stream',
                        'Stream',
                        'Enter stream (e.g., Science, Commerce)'
                    )}
                    {renderTextInput(
                        '10thGrade',
                        'syllabus',
                        'Syllabus',
                        'Enter syllabus (e.g., CBSE, ICSE)'
                    )}
                </div>

                {/* 12th Grade Section */}
                <label className={`${labelClass} mt-6`}>12th Grade - Year, Percentage, Stream & Syllabus</label>
                <div className="mt-6 grid grid-cols-4 gap-6 w-[90%] mb-8">
                    {renderYearInput(
                        '12thGrade',
                        'Year',
                        'Enter year (e.g., 2022)'
                    )}
                    {renderPercentageInput(
                        '12thGrade',
                        'Percentage',
                        'Enter percentage'
                    )}
                    {renderTextInput(
                        '12thGrade',
                        'stream',
                        'Stream',
                        'Enter stream (e.g., Science, Commerce)'
                    )}
                    {renderTextInput(
                        '12thGrade',
                        'syllabus',
                        'Syllabus',
                        'Enter syllabus (e.g., CBSE, ICSE)'
                    )}
                </div>

                {/* Highest Level of Education */}
                <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                        <label className={labelClass}>Highest Level of Education</label>
                        {write && (
                            <button
                                type="button"
                                onClick={addHighestEducationEntry}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <span className="text-lg font-bold">+</span>
                                Add Education
                            </button>
                        )}
                    </div>
                    
                    {academicData.highestEducation.map((education, index) => (
                        <div key={education.id} className="mb-6 p-4 border border-gray-200 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-lg font-medium text-gray-700">
                                    Education {index + 1}
                                </h4>
                                {write && academicData.highestEducation.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeHighestEducationEntry(education.id)}
                                        className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                                    >
                                        <span>×</span>
                                        Remove
                                    </button>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-3 gap-6 mb-8">
                                <div>
                                    <label className={labelClass}>Education Level</label>
                                    <input
                                        className={inputClass}
                                        placeholder="Bachelors"
                                        value={education.name}
                                        onChange={(e) => handleHighestEducationChange(education.id, 'name', e.target.value)}
                                        readOnly={!write}
                                        disabled={!write}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Completion Year</label>
                                    <div className="relative">
                                        <input
                                            className={`${inputClass} ${educationYearErrors[education.id] ? 'border-red-500 focus:ring-red-500' : ''}`}
                                            placeholder="Enter year (e.g., 2016)"
                                            value={education.completionYear}
                                            onChange={(e) => handleEducationYearChange(education.id, e.target.value)}
                                            onBlur={(e) => validateEducationYear(education.id, e.target.value)}
                                            maxLength={4}
                                            readOnly={!write}
                                            disabled={!write}
                                        />
                                        {educationYearErrors[education.id] && (
                                            <div className="absolute -bottom-6 left-0 text-red-500 text-sm">
                                                Year must be exactly 4 digits
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Obtained Marks</label>
                                    <input
                                        className={inputClass}
                                        placeholder="398/480"
                                        value={education.marksObtained}
                                        onChange={(e) => handleHighestEducationChange(education.id, 'marksObtained', e.target.value)}
                                        readOnly={!write}
                                        disabled={!write}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Study Preference Section */}
                <div>
                    <h3 className="text-xl mt-6 font-semibold mb-4">Study Preference</h3>
                    <div className="grid grid-cols-3 gap-6 w-[90%]">
                        {/* Preferred Destination Dropdown */}
                        <div className="relative" ref={destinationRef}>
                            <label className={labelClass}>Preferred Destination</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={countrySearchTerm || studyPreference.destinationName}
                                    onChange={handleCountrySearchChange}
                                    onClick={() => setIsDestinationDropdownOpen(!isDestinationDropdownOpen)}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                    onClick={() => setIsDestinationDropdownOpen(!isDestinationDropdownOpen)}
                                    disabled={!write}
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                        alt="Dropdown Arrow"
                                        className={`w-7 h-7 transition-transform ${isDestinationDropdownOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {isDestinationDropdownOpen && (
                                    <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-20 max-h-[300px] overflow-hidden">
                                        <div className="p-2">
                                            <div className="space-y-1 max-h-[280px] overflow-y-auto">
                                                {isLoadingCountry ? (
                                                    <div className="px-4 py-2 text-gray-500">Loading...</div>
                                                ) : countries.length > 0 ? (
                                                    countries.map((country) => (
                                                        <button
                                                            key={country.id}
                                                            className="px-4 py-2 text-left hover:bg-gray-100 rounded-xl cursor-pointer text-sm w-full"
                                                            onClick={() => {
                                                                setStudyPreference(prev => ({
                                                                    ...prev,
                                                                    destinationId: country.id,
                                                                    destinationName: country.name
                                                                }));
                                                                setCountrySearchTerm('');
                                                                setIsDestinationDropdownOpen(false);
                                                            }}>
                                                            <div className="flex justify-between items-center text-base">
                                                                {country.name}
                                                            </div>
                                                        </button>
                                                    ))
                                                ) : countrySearchTerm.length < 3 ? (
                                                    <div className="px-4 py-2 text-gray-500">Type at least 3 characters to search</div>
                                                ) : (
                                                    <div className="px-4 py-2 text-gray-500">No countries found</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Intended Course Level */}
                        <div>
                            <label className={labelClass}>Intended Course Level</label>
                            <input
                                type="text"
                                value={studyPreference.intendedCourseLevel}
                                onChange={(e) => setStudyPreference(prev => ({
                                    ...prev,
                                    intendedCourseLevel: e.target.value
                                }))}
                                placeholder="Enter course level"
                                className={inputClass}
                                readOnly={!write}
                                disabled={!write}
                            />
                        </div>

                        {/* Intended Area of Study */}
                        <div>
                            <label className={labelClass}>Intended Area of Study</label>
                            <input
                                type="text"
                                value={studyPreference.intendedAreaOfStudy}
                                onChange={(e) => setStudyPreference(prev => ({
                                    ...prev,
                                    intendedAreaOfStudy: e.target.value
                                }))}
                                placeholder="Enter area of study"
                                className={inputClass}
                                readOnly={!write}
                                disabled={!write}
                            />
                        </div>
                    </div>

                    {/* Intake Selection */}
                    <div className="mt-4 grid grid-cols-2 gap-6 w-[60%]">
                        {/* Month Dropdown */}
                        <div className="relative" ref={intakeMonthRef}>
                            <label className={labelClass}>Intake Month</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={studyPreference.intakeMonth || "-- Select Month --"}
                                    onClick={() => setIsIntakeMonthDropdownOpen(!isIntakeMonthDropdownOpen)}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                    onClick={() => setIsIntakeMonthDropdownOpen(!isIntakeMonthDropdownOpen)}
                                    disabled={!write}
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                        alt="Dropdown Arrow"
                                        className={`w-7 h-7 transition-transform ${isIntakeMonthDropdownOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {isIntakeMonthDropdownOpen && (
                                    <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10 max-h-40 overflow-y-auto">
                                        {months.map((month) => (
                                            <div
                                                key={month}
                                                className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                                                onClick={() => {
                                                    setStudyPreference(prev => ({
                                                        ...prev,
                                                        intakeMonth: month
                                                    }));
                                                    setIsIntakeMonthDropdownOpen(false);
                                                }}
                                            >
                                                {month}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Year Dropdown */}
                        <div className="relative" ref={intakeYearRef}>
                            <label className={labelClass}>Intake Year</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={studyPreference.intakeYear || "-- Select Year --"}
                                    onClick={() => setIsIntakeYearDropdownOpen(!isIntakeYearDropdownOpen)}
                                    className={inputClass}
                                    readOnly={!write}
                                    disabled={!write}
                                />
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                                    onClick={() => setIsIntakeYearDropdownOpen(!isIntakeYearDropdownOpen)}
                                    disabled={!write}
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/light-arrow-drop-down.svg`}
                                        alt="Dropdown Arrow"
                                        className={`w-7 h-7 transition-transform ${isIntakeYearDropdownOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {isIntakeYearDropdownOpen && (
                                    <div className="absolute left-0 right-0 mt-1 bg-white border rounded-3xl shadow-lg z-10 max-h-40 overflow-y-auto">
                                        {intakeYears.map((year) => (
                                            <div
                                                key={year}
                                                className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                                                onClick={() => {
                                                    setStudyPreference(prev => ({
                                                        ...prev,
                                                        intakeYear: year
                                                    }));
                                                    setIsIntakeYearDropdownOpen(false);
                                                }}
                                            >
                                                {year}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Save Button */}
                {write && (
                    <div className="flex items-center justify-center h-full">
                        <button
                            onClick={confirmSave}
                            disabled={isLoading}
                            className={`text-center p-3 text-white bg-gradient-to-r from-[#3483BE] to-[#02385E] mt-5 mb-5 rounded-3xl font-medium hover:bg-gray-50 relative ${isLoading ? 'cursor-not-allowed opacity-75' : ''
                                }`}
                        >
                            <span className={isLoading ? 'invisible' : ''}>Save & Proceed</span>
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                                </div>
                            )}
                        </button>
                    </div>
                )}
            </div>


            <ConfirmationModal isOpen={isConfirmModalOpen} isAdditional={isAdditionalActions} onClose={() => setIsConfirmModalOpen(false)} onSave={handleSave}
                selectedStudent={{ id: leadId, name: leadName, phone: leadPhone }} />

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default AcademicResults;